/**
 * Story 192 (issue #3040) — CU7 / RN20: the Description of a not planned resource, resolved by its reasonCode.
 *
 * Contract: mic-user-stories 192 · 03-dominio.md §"Contrato cross-repo congelado". GET
 * /endpoints/allNotPlannedResources/{id} brings, per row, an optional `reasonCode` (one of the codes below) and
 * `reasonParams` (an object of strings); phase G (2026-10-08) adds the two *_PLANNED_ASAP codes. Only one generic
 * message per code (GATE 2, H8): the English variant that Sun emitted in `description` does not matter, and neither
 * does the "The resource has not been fully planned. " prefix, which is never shown when the row is translated.
 *
 * Any other case (no code, unknown code, params missing) shows `description` as it is, as before the story.
 *
 * The keys are written as literals inside `t_(...)` (key = English text) so the /translate command and the
 * language catalogue find them.
 */

const REASONS = {
  NOT_ENOUGH_RAW_MATERIAL: {
    params: ['references'],
    dates: [],
    translate: (t_, params) => t_('Not enough raw material. References: {{references}}', params)
  },
  NOT_ENOUGH_PART_RESOURCES: {
    params: ['partReference'],
    dates: [],
    translate: (t_, params) => t_('Not enough parts. Part reference: {{partReference}}', params)
  },
  RAW_MATERIAL_LATE_FOR_JIT: {
    params: ['reference', 'availableAt', 'requiredAt'],
    dates: ['availableAt', 'requiredAt'],
    translate: (t_, params) => t_('Raw material {{reference}} will not be available in time for JIT: available at {{availableAt}}, required at {{requiredAt}}', params)
  },
  PART_RESOURCES_LATE_FOR_JIT: {
    params: ['partReference', 'availableAt', 'requiredAt'],
    dates: ['availableAt', 'requiredAt'],
    translate: (t_, params) => t_('Part {{partReference}} will not be available in time for JIT: available at {{availableAt}}, required at {{requiredAt}}', params)
  },
  // Phase G (2026-10-08): the JIT resource check failed, so Sun re-planned the whole FO ASAP and left this notice.
  RAW_MATERIAL_LATE_FOR_JIT_PLANNED_ASAP: {
    params: ['reference', 'availableAt', 'requiredAt'],
    dates: ['availableAt', 'requiredAt'],
    translate: (t_, params) => t_('Planned ASAP: raw material {{reference}} will not be available in time for JIT (available at {{availableAt}}, required at {{requiredAt}})', params)
  },
  PART_RESOURCES_LATE_FOR_JIT_PLANNED_ASAP: {
    params: ['partReference', 'availableAt', 'requiredAt'],
    dates: ['availableAt', 'requiredAt'],
    translate: (t_, params) => t_('Planned ASAP: part {{partReference}} will not be available in time for JIT (available at {{availableAt}}, required at {{requiredAt}})', params)
  }
}

const own = (object, name) => Object.prototype.hasOwnProperty.call(object, name)

const hasAllParams = (reason, reasonParams) =>
  reasonParams !== null && typeof reasonParams === 'object' &&
  reason.params.every((name) => own(reasonParams, name) && typeof reasonParams[name] === 'string')

/**
 * @param {Object} row - one element of GET allNotPlannedResources/{id}
 * @param {function(string, Object): string} t_ - translation function (useSmartTranslation)
 * @param {function(string): string} formatDateTime - useDateFormatter().formatDateTime
 * @returns {string} the text of the Description column
 */
export const resolveNotPlannedDescription = (row, t_, formatDateTime) => {
  const reason = own(REASONS, row.reasonCode) ? REASONS[row.reasonCode] : undefined
  if (!reason || !hasAllParams(reason, row.reasonParams)) return row.description
  const params = { ...row.reasonParams }
  reason.dates.forEach((name) => { params[name] = formatDateTime(row.reasonParams[name]) })
  return reason.translate(t_, params)
}
